import {makeStyles} from "@material-ui/core"

// Theme
import {ModernTheme} from "../../../../../theme/modern/tokens"
import {COARSE, touchTarget} from "../../../../../theme/modern/touchTargets"

export default makeStyles((theme: ModernTheme) => ({
    overlay: {
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "rgba(8,13,22,.46)",
    },
    /**
     * Sized to hold the WHOLE catalogue at once.
     *
     * It began as a 560 px column, which meant scrolling past a hundred
     * statements to reach the technical ones at the bottom — and a list you
     * have to scroll is a list you have to already know your way around. Wide
     * and short turns it into something you read rather than page through: the
     * sections sit side by side, so the shape of the catalogue is visible and
     * a statement is found by looking, not by searching.
     */
    card: {
        width: "min(1420px, 97vw)",
        maxHeight: "94vh",
        display: "flex",
        flexDirection: "column",
        background: theme.modern.panel,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 12,
        boxShadow: theme.modern.shadow,
        overflow: "hidden",
    },
    head: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "12px 14px 10px",
        borderBottom: `1px solid ${theme.modern.line}`,
    },
    microlabel: {
        font: `600 11px ${theme.modern.sans}`,
        letterSpacing: ".12em",
        textTransform: "uppercase",
        color: theme.modern.ink2,
    },
    close: {
        cursor: "pointer",
        border: "none",
        background: "none",
        color: theme.modern.ink3,
        font: `400 20px/1 ${theme.modern.sans}`,
        padding: "0 4px",

        "&:hover": {color: theme.modern.ink},

        [COARSE]: touchTarget({padding: "0 10px"}),
    },
    search: {
        margin: "10px 12px 6px",
        padding: "9px 11px",
        background: theme.modern.bg,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 7,
        color: theme.modern.ink,
        // 16px keeps iOS from zooming the page when the field takes focus
        font: `400 15px ${theme.modern.sans}`,

        "&:focus": {outline: "none", borderColor: theme.modern.accent},

        [COARSE]: {fontSize: 16},
    },
    /**
     * Newspaper columns rather than one long strip.
     *
     * The column count steps down with the viewport so the text never gets
     * narrower than a statement needs. `overflowY: auto` stays as a floor, not
     * as the plan: below about three columns the catalogue genuinely cannot fit
     * a phone screen, and being unable to reach the last section would be worse
     * than scrolling to it.
     */
    list: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: "4px 10px 10px",
        columnCount: 5,
        columnGap: 20,

        "@media (max-width:1400px)": {columnCount: 4},
        "@media (max-width:1080px)": {columnCount: 3},
        "@media (max-width:800px)": {columnCount: 2},
        "@media (max-width:560px)": {columnCount: 1},
    },
    /**
     * Sections are allowed to break across a column.
     *
     * `break-inside: avoid` was the obvious choice and the wrong one: Heart
     * rhythm holds 41 of the statements, so keeping it whole made a single
     * block taller than the sheet and forced the scrolling this change exists
     * to remove. Columns read top to bottom before moving right, so a section
     * continuing into the next column stays in reading order — and the rule
     * below keeps a heading from being the last thing in a column with its
     * statements stranded in the next.
     */
    group: {
        marginBottom: 11,
        display: "block",
    },
    group_title: {
        margin: "0 0 3px",
        padding: "0 4px 3px",
        font: `600 9.5px ${theme.modern.sans}`,
        letterSpacing: ".13em",
        textTransform: "uppercase",
        color: theme.modern.ink3,
        borderBottom: `1px solid ${theme.modern.line}`,
        // a heading alone at the foot of a column, its statements in the next,
        // reads as an empty section
        breakAfter: "avoid",
        WebkitColumnBreakAfter: "avoid",
        breakInside: "avoid",
    },
    row: {
        display: "flex",
        width: "100%",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: "10px",
        textAlign: "left",
        cursor: "pointer",
        /*
         * Opt out of the global `button { text-transform: uppercase }`, which
         * exists to make Modern's controls match Classic's MUI labels. These
         * rows are not labels — they are clinical sentences, several words
         * long, and "RIGHT INCOMPLETE BUNDLE BRANCH BLOCK" is markedly harder
         * to scan than the same line in sentence case. A class beats the
         * element selector on specificity, which is how that rule intends to
         * be overridden.
         */
        textTransform: "none",
        border: "1px solid transparent",
        background: "none",
        borderRadius: 5,
        // a pixel off each row is 216 px of linear content across 108 of them,
        // which is what brings a 1280 px laptop inside the no-scroll budget
        padding: "2px 6px",
        font: `400 11.5px/1.3 ${theme.modern.sans}`,
        color: theme.modern.ink2,

        [COARSE]: touchTarget({padding: "10px", height: "auto"}),
    },
    row__active: {
        background: theme.modern.accentSoft,
        borderColor: theme.modern.accentLine,
        color: theme.modern.ink,
    },
    /** Already in the text: still clickable, but visibly spent. */
    row__added: {
        color: theme.modern.ink3,
    },
    row_text: {
        minWidth: 0,
    },
    /**
     * Marks a statement that will land with its leads still to choose.
     *
     * Same pill as the typeahead's, for the same reason: it has to read as an
     * offer rather than as a footnote. It sits inline with the statement, so
     * it wraps with the text instead of drifting to the edge of the row.
     */
    slot: {
        display: "inline-block",
        marginLeft: 5,
        font: `600 8.5px/1 ${theme.modern.sans}`,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        color: theme.modern.accent,
        background: theme.modern.accentSoft,
        border: `1px solid ${theme.modern.accentLine}`,
        borderRadius: 99,
        padding: "2px 5px",
        verticalAlign: "baseline",
    },
    added: {
        flex: "none",
        font: `600 8.5px ${theme.modern.sans}`,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        color: theme.modern.ok,
    },
    empty: {
        margin: "18px 10px",
        font: `400 14px ${theme.modern.sans}`,
        color: theme.modern.ink3,
    },
    hint: {
        margin: 0,
        padding: "9px 14px",
        borderTop: `1px solid ${theme.modern.line}`,
        font: `400 12px ${theme.modern.sans}`,
        color: theme.modern.ink3,
    },
}))
